import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Responsive } from "react-grid-layout/legacy";
import { DashboardLayoutCard } from "./DashboardLayoutCard/DashboardLayoutCard";

import "./DashboardLayout.scss";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

export default function DashbordLayout({
  layout,
  handleBlur,
  handleDelete,
  change,
  editedLayout,
  view,
}) {
  const [breakpoints] = useState({
    lg: 1600,
    md: 1320,
    sm: 900,
    xs: 480,
    xxs: 0,
  });

  // Measure the real container width ourselves so the grid never renders
  // with WidthProvider's default 1280px fallback (which places all cards in
  // the left portion of wider containers before the async ResizeObserver fires).
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);

  // Synchronous: fires before the browser paints, so first visible render
  // already has the correct width — no left-half flash.
  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  // Keep width in sync as the container resizes.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Provide the same layout for every breakpoint so react-grid-layout never
  // auto-compacts on a breakpoint switch (which would rearrange cards and
  // change the container height, triggering another resize — the loop).
  const allLayouts = useMemo(
    () => ({ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }),
    [layout]
  );

  const cards = useMemo(() => {
    return layout?.map(({ static: s, i, ...rest }) => {
      return (
        <DashboardLayoutCard
          handleBlur={handleBlur}
          handleDelete={handleDelete}
          className={s ? "static" : ""}
          key={i}
          cardKey={i}
          title={rest.data.name}
          {...rest}
          change={change}
        />
      );
    });
  }, [layout, change]);

  const getView = (viewType) => {
    const types = {
      Admin: {
        rowHeight: 315,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      },
      Supervisor: {
        rowHeight: 315,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      },
      settingsPozicionet: {
        rowHeight: 315,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      },
      settingsFitimet: {
        rowHeight: 480,
        col: { lg: 5, md: 4, sm: 3, xs: 2, xxs: 1 },
      },
      settingsZbritjet: {
        rowHeight: 480,
        col: { lg: 5, md: 4, sm: 3, xs: 2, xxs: 1 },
      },
      departments: {
        rowHeight: 315,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      },
      profili: {
        rowHeight: 275,
        col: { lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 },
      },
      settingsInfoRrethKompanis: {
        rowHeight: 380,
        col: { lg: 2, md: 2, sm: 1, xs: 1, xxs: 1 },
      },
      draggableInterviewsCharts: {
        rowHeight: 370,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      },
    };
    return (
      types[viewType] || {
        rowHeight: 315,
        col: { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 },
      }
    );
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {containerWidth && (
        <Responsive
          {...{
            width: containerWidth,
            layouts: allLayouts,
            id: "dashboard-react-layout",
            className: "dashboard-react-layout",
            allowOverlap: false,
            breakpoints,
            cols: getView(view).col,
            rowHeight: getView(view).rowHeight,
            isBounded: false,
            margin: [10, 10],
            isDraggable: change,
            isResizable: change,
            resizeHandles: ["sw", "se"],
            useCSSTransforms: true,
            draggableHandle: ".header-dashbord-card-title-container",
            onLayoutChange(l) {
              editedLayout.current = l;
            },
          }}
        >
          {cards}
        </Responsive>
      )}
    </div>
  );
}
